import { CalendarEvent, DateType, Priority, EventMember } from '../types/calendar';
import { Todo, TodoPriority } from '../types/todo';
import { Family, FamilyMember } from '../store/familyStore';

/**
 * Mock data generators for design system component examples
 */

export function createMockFamily(): Family {
  return {
    familyId: 'mock-family-1',
    name: 'The Smiths',
    ownerId: 'user-1',
    location: {
      city: 'San Francisco',
      latitude: 37.7749,
      longitude: -122.4194,
    },
    members: [
      {
        userId: 'user-1',
        email: 'john@example.com',
        name: 'John Smith',
        role: 'owner',
        color: '#3b82f6',
        avatar: '',
        joinedAt: new Date().toISOString(),
      },
      {
        userId: 'user-2',
        email: 'jane@example.com',
        name: 'Jane Smith',
        role: 'parent',
        color: '#ef4444',
        avatar: '',
        joinedAt: new Date().toISOString(),
      },
      {
        userId: 'user-3',
        email: 'emma@example.com',
        name: 'Emma Smith',
        role: 'child',
        color: '#10b981',
        avatar: '',
        joinedAt: new Date().toISOString(),
      },
      {
        userId: 'user-4',
        email: 'lucas@example.com',
        name: 'Lucas Smith',
        role: 'child',
        color: '#8b5cf6',
        avatar: '',
        joinedAt: new Date().toISOString(),
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function createMockFamilyMembers(): FamilyMember[] {
  return createMockFamily().members;
}

export function createMockEvents(): CalendarEvent[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  return [
    {
      id: 'event-1',
      title: 'Soccer Practice',
      description: 'Weekly soccer practice at the park',
      startTime: new Date(today.getTime() + 16 * 60 * 60 * 1000), // 4:00 PM
      endTime: new Date(today.getTime() + 17 * 60 * 60 * 1000), // 5:00 PM
      memberId: 'member-3',
      memberName: 'Emma Smith',
      memberColor: '#10b981',
      memberIds: ['member-3'],
      members: [
        {
          memberId: 'member-3',
          memberName: 'Emma Smith',
          memberColor: '#10b981',
        },
      ],
      location: 'City Park',
      dateType: 'regular' as DateType,
      priority: 'medium' as Priority,
      isAllDay: false,
      isImportantDate: false,
      isRecurring: true,
      recurringPattern: {
        frequency: 'weekly',
        interval: 1,
      },
    },
    {
      id: 'event-2',
      title: 'Doctor Appointment',
      description: 'Annual checkup',
      startTime: new Date(today.getTime() + 10 * 60 * 60 * 1000), // 10:00 AM
      endTime: new Date(today.getTime() + 11 * 60 * 60 * 1000), // 11:00 AM
      memberId: 'member-2',
      memberName: 'Jane Smith',
      memberColor: '#ef4444',
      memberIds: ['member-2'],
      members: [
        {
          memberId: 'member-2',
          memberName: 'Jane Smith',
          memberColor: '#ef4444',
        },
      ],
      location: 'Medical Center',
      dateType: 'medical' as DateType,
      priority: 'high' as Priority,
      isAllDay: false,
      isImportantDate: false,
      isRecurring: false,
    },
    {
      id: 'event-3',
      title: 'Family Dinner',
      description: 'Dinner at home',
      startTime: new Date(today.getTime() + 18 * 60 * 60 * 1000), // 6:00 PM
      endTime: new Date(today.getTime() + 20 * 60 * 60 * 1000), // 8:00 PM
      memberId: 'member-1',
      memberName: 'John Smith',
      memberColor: '#3b82f6',
      memberIds: ['member-1', 'member-2'],
      members: [
        {
          memberId: 'member-1',
          memberName: 'John Smith',
          memberColor: '#3b82f6',
        },
        {
          memberId: 'member-2',
          memberName: 'Jane Smith',
          memberColor: '#ef4444',
        },
      ],
      location: 'Home',
      dateType: 'regular' as DateType,
      priority: 'medium' as Priority,
      isAllDay: false,
      isImportantDate: false,
      isRecurring: false,
    },
    {
      id: 'event-4',
      title: 'Birthday Party',
      description: 'Emma\'s 10th birthday celebration',
      startTime: new Date(today.getTime() + 14 * 60 * 60 * 1000), // 2:00 PM
      endTime: new Date(today.getTime() + 16 * 60 * 60 * 1000), // 4:00 PM
      memberId: 'member-3',
      memberName: 'Emma Smith',
      memberColor: '#10b981',
      memberIds: ['member-3'],
      members: [
        {
          memberId: 'member-3',
          memberName: 'Emma Smith',
          memberColor: '#10b981',
        },
      ],
      location: 'Home',
      dateType: 'birthday' as DateType,
      priority: 'high' as Priority,
      isAllDay: false,
      isImportantDate: true,
      isRecurring: false,
    },
    {
      id: 'event-5',
      title: 'All Day Conference',
      description: 'Tech conference downtown',
      startTime: new Date(today.getTime()),
      endTime: new Date(today.getTime() + 24 * 60 * 60 * 1000),
      memberId: 'member-1',
      memberName: 'John Smith',
      memberColor: '#3b82f6',
      memberIds: ['member-1'],
      members: [
        {
          memberId: 'member-1',
          memberName: 'John Smith',
          memberColor: '#3b82f6',
        },
      ],
      location: 'Convention Center',
      dateType: 'regular' as DateType,
      priority: 'medium' as Priority,
      isAllDay: true,
      isImportantDate: false,
      isRecurring: false,
    },
  ];
}

export function createMockTodos(): Todo[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  return [
    {
      todoId: 'todo-1',
      familyId: 'mock-family-1',
      title: 'Buy groceries',
      description: 'Milk, eggs, bread, vegetables',
      status: 'open',
      priority: 'medium' as TodoPriority,
      dueDateTime: tomorrow,
      listId: 'list-1',
      assigneeId: 'member-2',
      assigneeName: 'Jane Smith',
      isFamily: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      todoId: 'todo-2',
      familyId: 'mock-family-1',
      title: 'Finish homework',
      description: 'Math and science assignments',
      status: 'open',
      priority: 'high' as TodoPriority,
      dueDateTime: today,
      listId: 'list-2',
      assigneeId: 'member-3',
      assigneeName: 'Emma Smith',
      isFamily: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      todoId: 'todo-3',
      familyId: 'mock-family-1',
      title: 'Call dentist',
      description: 'Schedule appointment',
      status: 'done',
      priority: 'medium' as TodoPriority,
      dueDateTime: today,
      listId: 'list-3',
      assigneeId: 'member-1',
      assigneeName: 'John Smith',
      isFamily: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  ];
}

export function createMockTodoLists() {
  return [
    { listId: 'list-1', name: 'Shopping' },
    { listId: 'list-2', name: 'School' },
    { listId: 'list-3', name: 'Health' },
    { listId: 'list-4', name: 'Home' },
  ];
}

export function createMockWeather() {
  return {
    location: {
      name: 'San Francisco',
      region: 'California',
      country: 'USA',
      lat: 37.7749,
      lon: -122.4194,
    },
    current: {
      tempC: 18,
      tempF: 64,
      condition: {
        text: 'Partly cloudy',
        icon: '//cdn.weatherapi.com/weather/64x64/day/116.png',
      },
      humidity: 65,
      windMph: 8,
      windKph: 13,
      feelslikeC: 17,
      feelslikeF: 63,
    },
    forecast: {
      forecastday: [
        {
          date: new Date().toISOString().split('T')[0],
          day: {
            maxtempC: 20,
            maxtempF: 68,
            mintempC: 15,
            mintempF: 59,
            condition: {
              text: 'Sunny',
              icon: '//cdn.weatherapi.com/weather/64x64/day/113.png',
            },
          },
          hour: Array.from({ length: 24 }, (_, i) => ({
            time: new Date(new Date().setHours(i, 0, 0, 0)).toISOString(),
            tempC: 15 + Math.sin(i / 24 * Math.PI * 2) * 5,
            tempF: 59 + Math.sin(i / 24 * Math.PI * 2) * 9,
            condition: {
              text: i < 6 || i > 20 ? 'Clear' : 'Sunny',
              icon: '//cdn.weatherapi.com/weather/64x64/day/113.png',
            },
          })),
        },
      ],
    },
    lastUpdated: Date.now(),
  };
}
